import { useEffect, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { CreditCard, FileText, Settings, Plus, Pencil, Trash2, Loader2, FileSignature, Percent, Unlink, MessageCircle, FileDown, ChevronDown, Users } from "lucide-react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import {
  listPlans, upsertPlan, deletePlan, listContracts,
  listStudentsForContract, upsertContract, unlinkContract,
} from "@/lib/api/plans.functions";
import { ContractDialog } from "@/components/contracts/ContractDialog";
import { ContractTemplateConfigDialog } from "@/components/contracts/ContractTemplateConfigDialog";
import { BulkContractDialog } from "@/components/contracts/BulkContractDialog";
import { PostContractActionModal } from "@/components/contracts/PostContractActionModal";
import { sendContractPdfViaWhatsapp } from "@/lib/api/whatsapp.functions";

export const Route = createFileRoute("/_authenticated/m/planos-pagamento")({
  component: PlanosPage,
});

import { PAYMENT_METHODS, paymentMethodLabel as methodLabel, isMercadoPagoMethod } from "@/lib/payment-methods";
import { MercadoPagoBadge } from "@/components/shared/MercadoPagoBadge";
import { PaymentMethodsPicker } from "@/components/shared/PaymentMethodsPicker";
import { brToday } from "@/lib/tz";

type Plan = {
  id: string;
  plan_type: string;
  price: number;
  validity: string;
  accepted_payment_methods: string[];
  due_type?: "offset" | "fixed_day" | null;
  due_offset_days?: number | null;
  due_fixed_day?: number | null;
  keep_active_until_paid?: boolean | null;
  is_social_project?: boolean | null;
  visible_to_parents?: boolean | null;
  allow_self_enroll?: boolean | null;
  is_trial?: boolean | null;
  trial_days_of_week?: number[] | null;
  trial_specific_date?: string | null;
};

type Contract = {
  id: string;
  name: string;
  cpf: string | null;
  birth_date: string | null;
  status: string | null;
  payment_status: string | null;
  plan_price_id: string | null;
  payment_method: string | null;
  plan_discount: number | null;
  plan_start_date: string | null;
  plan_end_date: string | null;
  plan_auto_renew?: boolean | null;
  plan_prices: { id: string; plan_type: string; price: number; validity: string; accepted_payment_methods: string[]; due_type?: string | null; due_offset_days?: number | null; due_fixed_day?: number | null; keep_active_until_paid?: boolean | null; is_social_project?: boolean | null } | null;
  parents: { id: string; name: string; cpf: string | null; phone: string | null; email: string | null; street: string | null; number: string | null; neighborhood: string | null; city: string | null; state: string | null; cep: string | null } | null;
};

type StudentOption = {
  id: string;
  name: string;
  category?: string | null;
  status?: string | null;
  plan_price_id: string | null;
  plan_end_date?: string | null;
  parents: { id: string; name: string } | null;
};

function PlanosPage() {
  const fetchPlans = useServerFn(listPlans);
  const fetchContracts = useServerFn(listContracts);
  const fetchStudents = useServerFn(listStudentsForContract);
  const save = useServerFn(upsertPlan);
  const remove = useServerFn(deletePlan);
  const saveContract = useServerFn(upsertContract);
  const removeContract = useServerFn(unlinkContract);

  const [plans, setPlans] = useState<Plan[]>([]);
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [scheduled, setScheduled] = useState<any[]>([]);
  const [students, setStudents] = useState<StudentOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Plan | null>(null);
  const [saving, setSaving] = useState(false);
  const [contractFor, setContractFor] = useState<Contract | null>(null);
  const [form, setForm] = useState({
    plan_type: "",
    price: "",
    accepted_payment_methods: [] as string[],
    due_type: "offset" as "offset" | "fixed_day",
    due_offset_days: "0",
    due_fixed_day: "10",
    keep_active_until_paid: false,
    is_social_project: false,
    visible_to_parents: true,
    allow_self_enroll: false,
    is_trial: false,
    trial_days_of_week: [] as number[],
    trial_specific_date: "",
  });

  // Novo Contrato
  const [contractDialogOpen, setContractDialogOpen] = useState(false);
  const [editingContractStudentId, setEditingContractStudentId] = useState<string | null>(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [tplConfigOpen, setTplConfigOpen] = useState(false);
  const [savingContract, setSavingContract] = useState(false);
  const [contractForm, setContractForm] = useState({
    student_id: "",
    plan_price_id: "",
    payment_method: "",
    discount: "0",
    start_date: brToday(),
    end_date: "",
    auto_renew: true,
  });
  const [onlyWithoutContract, setOnlyWithoutContract] = useState(false);
  const [postActionStudent, setPostActionStudent] = useState<string | null>(null);
  const [sendingPdfFor, setSendingPdfFor] = useState<string | null>(null);

  // ---- Filtros dos contratos ----
  const [fStudent, setFStudent] = useState("");
  const [fParent, setFParent] = useState("");
  const [fPlan, setFPlan] = useState("all");
  const [fValidity, setFValidity] = useState("all"); // vigente | expirado
  const [fStatus, setFStatus] = useState("all"); // ativo | inativo
  const [fPayment, setFPayment] = useState("all"); // pago | pendente
  const [fDueFrom, setFDueFrom] = useState("");
  const [fDueTo, setFDueTo] = useState("");

  const norm = (s: string) =>
    (s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

  const filteredContracts = contracts.filter((c) => {
    if (fStudent && !norm(c.name).includes(norm(fStudent))) return false;
    if (fParent && !norm(c.parents?.name ?? "").includes(norm(fParent))) return false;
    if (fPlan !== "all" && c.plan_price_id !== fPlan) return false;
    if (fStatus !== "all") {
      const ativo = c.status === "ativo" || c.status === "active";
      if (fStatus === "ativo" ? !ativo : ativo) return false;
    }
    if (fPayment !== "all") {
      const pago = c.payment_status === "pago";
      if (fPayment === "pago" ? !pago : pago) return false;
    }
    const today = brToday();
    if (fValidity !== "all") {
      const vigente = !!c.plan_end_date && c.plan_end_date >= today;
      if (fValidity === "vigente" ? !vigente : vigente) return false;
    }
    if (fDueFrom && (!c.plan_end_date || c.plan_end_date < fDueFrom)) return false;
    if (fDueTo && (!c.plan_end_date || c.plan_end_date > fDueTo)) return false;
    return true;
  });

  const clearFilters = () => {
    setFStudent(""); setFParent(""); setFPlan("all"); setFValidity("all");
    setFStatus("all"); setFPayment("all"); setFDueFrom(""); setFDueTo("");
  };

  const sendPdfWa = useServerFn(sendContractPdfViaWhatsapp);

  const handleSendContractPdf = async (studentId: string) => {
    setSendingPdfFor(studentId);
    try {
      await sendPdfWa({ data: { studentId } });
      toast.success("Contrato em PDF enviado pelo WhatsApp!");
    } catch (e: any) {
      toast.error(e?.message || "Erro ao enviar contrato");
    } finally {
      setSendingPdfFor(null);
    }
  };

  const reload = async () => {
    setLoading(true);
    try {
      const [p, c, s] = await Promise.all([fetchPlans(), fetchContracts(), fetchStudents()]);
      setPlans((p.plans ?? []) as Plan[]);
      setContracts((c.contracts ?? []) as unknown as Contract[]);
      setScheduled(((c as any).scheduled ?? []) as any[]);
      setStudents((s.students ?? []) as unknown as StudentOption[]);
    } catch (e: any) {
      toast.error(e?.message ?? "Erro ao carregar");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []);

  const addMonthsAnniversary = (d: Date, months: number) => {
    const day = d.getDate();
    const target = new Date(d.getFullYear(), d.getMonth() + months, 1);
    const lastDayOfTargetMonth = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate();
    target.setDate(Math.min(day, lastDayOfTargetMonth));
    return target;
  };

  const computeEndDate = (start: string, validity: string): string => {
    if (!start) return "";
    const d = new Date(`${start}T00:00:00`);
    if (isNaN(d.getTime())) return "";
    let out = new Date(d);
    switch ((validity || "mensal").toLowerCase()) {
      case "diario": out.setDate(d.getDate() + 1); break;
      case "semanal": out.setDate(d.getDate() + 7); break;
      case "mensal": out = addMonthsAnniversary(d, 1); break;
      case "trimestral": out = addMonthsAnniversary(d, 3); break;
      case "semestral": out = addMonthsAnniversary(d, 6); break;
      case "anual": out = addMonthsAnniversary(d, 12); break;
      default: out = addMonthsAnniversary(d, 1);
    }
    return out.toISOString().slice(0, 10);
  };

  const openNewContract = () => {
    const today = brToday();
    setEditingContractStudentId(null);
    setContractForm({
      student_id: "",
      plan_price_id: "",
      payment_method: "",
      discount: "0",
      start_date: today,
      end_date: "",
      auto_renew: true,
    });
    setContractDialogOpen(true);
  };

  const openEditContract = (c: Contract) => {
    const today = brToday();
    const validity = c.plan_prices?.validity ?? "mensal";
    const start = today;
    setEditingContractStudentId(c.id);
    setContractForm({
      student_id: c.id,
      plan_price_id: c.plan_price_id ?? "",
      payment_method: c.payment_method ?? "",
      discount: String(c.plan_discount ?? 0),
      start_date: start,
      end_date: computeEndDate(start, validity),
      auto_renew: c.plan_auto_renew ?? true,
    });
    setContractDialogOpen(true);
  };


  const submitContract = async () => {
    if (!contractForm.student_id || !contractForm.plan_price_id || !contractForm.payment_method) {
      toast.error("Selecione aluno, plano e forma de pagamento.");
      return;
    }
    if (!contractForm.start_date || !contractForm.end_date) {
      toast.error("Informe as datas de início e fim do contrato.");
      return;
    }
    if (contractForm.end_date < contractForm.start_date) {
      toast.error("A data final deve ser posterior à data inicial.");
      return;
    }
    setSavingContract(true);
    try {
      const chosen = plans.find((p) => p.id === contractForm.plan_price_id);
      const dueAmount = Math.max(
        0,
        Number(chosen?.price ?? 0) - Number(contractForm.discount || 0),
      );
      const settled = !!chosen?.is_social_project || dueAmount <= 0;
      await saveContract({
        data: {
          student_id: contractForm.student_id,
          plan_price_id: contractForm.plan_price_id,
          payment_method: contractForm.payment_method,
          discount: Number(contractForm.discount || 0),
          start_date: contractForm.start_date,
          end_date: contractForm.end_date,
          auto_renew: contractForm.auto_renew,
          override_overlap: editingContractStudentId === contractForm.student_id,
        },
      });
      toast.success(
        settled ? "Contrato vinculado e quitado (R$ 0,00)!" : "Contrato vinculado!",
      );
      const sid = contractForm.student_id;
      setContractDialogOpen(false);
      reload();
      // Dispara fluxo pós-contrato (WhatsApp ou Caixa) — não se já quitado
      if (!settled) setPostActionStudent(sid);

    } catch (e: any) {
      toast.error(e?.message ?? "Erro ao salvar contrato");
    } finally {
      setSavingContract(false);
    }
  };

  const onUnlink = async (c: Contract) => {
    if (!confirm(`Excluir o plano de "${c.name}"?`)) return;
    try {
      await removeContract({ data: { student_id: c.id } });
      toast.success("Plano desvinculado");
      reload();
    } catch (e: any) {
      toast.error(e?.message ?? "Erro ao desvincular");
    }
  };

  const openNew = () => {
    setEditing(null);
    setForm({
      plan_type: "", price: "", accepted_payment_methods: [],
      due_type: "offset", due_offset_days: "0", due_fixed_day: "10", keep_active_until_paid: false,
      is_social_project: false,
      visible_to_parents: true, allow_self_enroll: false,
      is_trial: false, trial_days_of_week: [], trial_specific_date: "",
    });
    setDialogOpen(true);
  };

  const openEdit = (plan: Plan) => {
    setEditing(plan);
    setForm({
      plan_type: plan.plan_type,
      price: String(plan.price),
      accepted_payment_methods: plan.accepted_payment_methods ?? [],
      due_type: (plan.due_type ?? "offset") as "offset" | "fixed_day",
      due_offset_days: String(plan.due_offset_days ?? 0),
      due_fixed_day: String(plan.due_fixed_day ?? 10),
      keep_active_until_paid: !!plan.keep_active_until_paid,
      is_social_project: !!plan.is_social_project,
      visible_to_parents: plan.visible_to_parents !== false,
      allow_self_enroll: plan.allow_self_enroll === true,
      is_trial: !!plan.is_trial,
      trial_days_of_week: (plan.trial_days_of_week ?? []) as number[],
      trial_specific_date: plan.trial_specific_date ?? "",
    });
    setDialogOpen(true);
  };

  const submit = async () => {
    const isFree = form.is_social_project || form.is_trial;
    if (!form.plan_type.trim() || (!isFree && !form.price)) {
      toast.error("Preencha nome e preço");
      return;
    }
    setSaving(true);
    try {
      await save({
        data: {
          id: editing?.id ?? null,
          plan_type: form.plan_type.trim(),
          price: isFree ? 0 : Number(form.price),
          validity: "mensal",
          accepted_payment_methods: form.accepted_payment_methods,
          due_type: form.due_type,
          due_offset_days: Number(form.due_offset_days || 0),
          due_fixed_day: form.due_type === "fixed_day" ? Number(form.due_fixed_day || 10) : null,
          keep_active_until_paid: isFree ? false : form.keep_active_until_paid,
          is_social_project: form.is_social_project,
          visible_to_parents: form.visible_to_parents,
          allow_self_enroll: form.allow_self_enroll,
          is_trial: form.is_trial,
          trial_days_of_week: form.trial_days_of_week,
          trial_specific_date: form.trial_specific_date || null,
        },
      });

      toast.success(editing ? "Plano atualizado" : "Plano criado");
      setDialogOpen(false);
      reload();
    } catch (e: any) {
      toast.error(e?.message ?? "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (plan: Plan) => {
    if (!confirm(`Excluir o plano "${plan.plan_type}"?`)) return;
    try {
      await remove({ data: { id: plan.id } });
      toast.success("Plano excluído");
      reload();
    } catch (e: any) {
      toast.error(e?.message ?? "Erro ao excluir");
    }
  };

  const fmtBRL = (n: number) =>
    new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(n);

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <CreditCard className="h-7 w-7 text-primary" />
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Planos e Contratos</h1>
          <p className="text-muted-foreground text-sm">
            Gerencie seus planos de pagamento e contratos de alunos.
          </p>
        </div>
      </div>

      <Tabs defaultValue="ativos" className="space-y-6">
        <TabsList className="flex w-full">
          <TabsTrigger value="ativos" className="flex-1">
            <FileText className="mr-2 h-4 w-4" /> Contratos Ativos
          </TabsTrigger>
          <TabsTrigger value="planos" className="flex-1">
            <Settings className="mr-2 h-4 w-4" /> Configuração de Planos
          </TabsTrigger>
        </TabsList>

        <TabsContent value="ativos">
          <Card>
            <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <CardTitle>Contratos de Alunos</CardTitle>
                <CardDescription>
                  Visão geral dos contratos ativos e status de pagamento.
                </CardDescription>
              </div>
              <div className="flex flex-wrap gap-2 shrink-0">
                <Button variant="outline" onClick={() => setTplConfigOpen(true)}>
                  <FileDown className="mr-2 h-4 w-4" /> Contrato em PDF
                </Button>
                <Button variant="outline" onClick={() => setBulkOpen(true)}>
                  <Users className="mr-2 h-4 w-4" /> Atribuir em massa
                </Button>
                <Button onClick={openNewContract}>
                  <Plus className="mr-2 h-4 w-4" /> Novo Contrato
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {!loading && contracts.length > 0 && (
                <div className="mb-5 rounded-xl border bg-muted/30 p-3 space-y-3">
                  <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                    <div className="grid gap-1">
                      <Label className="text-xs">Aluno</Label>
                      <Input placeholder="Nome do aluno" value={fStudent} onChange={(e) => setFStudent(e.target.value)} />
                    </div>
                    <div className="grid gap-1">
                      <Label className="text-xs">Responsável</Label>
                      <Input placeholder="Nome do responsável" value={fParent} onChange={(e) => setFParent(e.target.value)} />
                    </div>
                    <div className="grid gap-1">
                      <Label className="text-xs">Plano</Label>
                      <Select value={fPlan} onValueChange={setFPlan}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Todos os planos</SelectItem>
                          {plans.map((p) => (
                            <SelectItem key={p.id} value={p.id}>{p.plan_type}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid gap-1">
                      <Label className="text-xs">Vigência</Label>
                      <Select value={fValidity} onValueChange={setFValidity}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Todas</SelectItem>
                          <SelectItem value="vigente">Vigente</SelectItem>
                          <SelectItem value="expirado">Expirado</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid gap-1">
                      <Label className="text-xs">Situação do aluno</Label>
                      <Select value={fStatus} onValueChange={setFStatus}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Todas</SelectItem>
                          <SelectItem value="ativo">Ativo</SelectItem>
                          <SelectItem value="inativo">Inativo</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid gap-1">
                      <Label className="text-xs">Pagamento</Label>
                      <Select value={fPayment} onValueChange={setFPayment}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Todos</SelectItem>
                          <SelectItem value="pago">Pago</SelectItem>
                          <SelectItem value="pendente">Pendente</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid gap-1">
                      <Label className="text-xs">Vencimento de</Label>
                      <Input type="date" value={fDueFrom} onChange={(e) => setFDueFrom(e.target.value)} />
                    </div>
                    <div className="grid gap-1">
                      <Label className="text-xs">Vencimento até</Label>
                      <Input type="date" value={fDueTo} onChange={(e) => setFDueTo(e.target.value)} />
                    </div>
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs text-muted-foreground">
                      {filteredContracts.length} de {contracts.length} contrato(s)
                    </span>
                    <Button variant="ghost" size="sm" onClick={clearFilters}>Limpar filtros</Button>
                  </div>
                </div>
              )}
              {loading ? (
                <div className="flex justify-center py-10">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : contracts.length === 0 ? (
                <div className="text-center py-12 border-2 border-dashed rounded-xl">
                  <p className="text-muted-foreground">
                    Nenhum aluno com plano vinculado.
                  </p>
                  <Button variant="link" onClick={openNewContract} className="mt-2">
                    Criar primeiro contrato
                  </Button>
                </div>
              ) : filteredContracts.length === 0 ? (
                <div className="text-center py-12 border-2 border-dashed rounded-xl">
                  <p className="text-muted-foreground">Nenhum contrato corresponde aos filtros.</p>
                  <Button variant="link" onClick={clearFilters} className="mt-2">Limpar filtros</Button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filteredContracts.map((c) => {

                    const base = Number(c.plan_prices?.price ?? 0);
                    const disc = Number(c.plan_discount ?? 0);
                    const finalPrice = Math.max(0, base - disc);
                    const fmtDate = (s?: string | null) => {
                      if (!s) return null;
                      const d = new Date(`${s}T00:00:00`);
                      return isNaN(d.getTime()) ? null : d.toLocaleDateString("pt-BR");
                    };
                    const computeDue = (): string | null => {
                      const pp = c.plan_prices;
                      if (!pp) return null;
                      if (pp.due_type === "fixed_day" && pp.due_fixed_day) {
                        const today = new Date();
                        const d = new Date(today.getFullYear(), today.getMonth(), pp.due_fixed_day);
                        if (d < today) d.setMonth(d.getMonth() + 1);
                        return d.toLocaleDateString("pt-BR");
                      }
                      if (c.plan_end_date) {
                        const d = new Date(`${c.plan_end_date}T00:00:00`);
                        d.setDate(d.getDate() + Number(pp.due_offset_days ?? 0));
                        return d.toLocaleDateString("pt-BR");
                      }
                      return null;
                    };
                    const startFmt = fmtDate(c.plan_start_date);
                    const endFmt = fmtDate(c.plan_end_date);
                    const dueFmt = computeDue();
                    return (
                      <Card key={c.id} className="border">
                        <CardContent className="pt-5 pb-4 space-y-2">
                          <div className="font-semibold">{c.name}</div>
                          {c.parents?.name ? (
                            <div className="text-xs text-muted-foreground">
                              Responsável: {c.parents.name}
                            </div>
                          ) : null}
                          <div className="text-sm text-muted-foreground">
                            Plano: {c.plan_prices?.plan_type ?? "—"} · {fmtBRL(finalPrice)}
                            {disc > 0 && (
                              <span className="ml-1 text-emerald-700">
                                (desc. {fmtBRL(disc)})
                              </span>
                            )}
                          </div>
                          {(startFmt || endFmt) && (
                            <div className="text-xs text-muted-foreground">
                              Vigência: <strong className="text-foreground">{startFmt ?? "—"}</strong> a <strong className="text-foreground">{endFmt ?? "—"}</strong>
                              {c.plan_auto_renew && (
                                <span className="ml-1 text-emerald-700">· renova automaticamente</span>
                              )}
                            </div>
                          )}
                          {c.plan_prices?.is_social_project && (
                            <div className="text-xs text-emerald-700">Projeto social — isento (R$ 0,00)</div>
                          )}
                          {dueFmt && (
                            <div className="text-xs text-muted-foreground">
                              Vencimento: <strong className="text-foreground">{dueFmt}</strong>
                              {c.plan_prices?.keep_active_until_paid && (
                                <span className="ml-1 text-emerald-700">(mantém ativo até pagar)</span>
                              )}
                            </div>
                          )}
                          {c.payment_method && (
                            <div className="text-xs text-muted-foreground">
                              Pagamento: {methodLabel(c.payment_method)}
                            </div>
                          )}
                          <div className="flex gap-2 pt-1">
                            <Badge
                              variant="outline"
                              className={
                                c.status === "ativo"
                                  ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                                  : "bg-slate-100 text-slate-600"
                              }
                            >
                              {c.status === "ativo" ? "Ativo" : "Inativo"}
                            </Badge>
                            <Badge
                              variant="outline"
                              className={
                                c.payment_status === "pago"
                                  ? "bg-blue-50 text-blue-700 border-blue-200"
                                  : "bg-amber-50 text-amber-700 border-amber-200"
                              }
                            >
                              {c.payment_status === "pago" ? "Pago" : "Pendente"}
                            </Badge>
                          </div>
                          <div className="flex flex-wrap gap-2 mt-2">
                            <Button
                              size="sm"
                              variant="outline"
                              className="flex-1 min-w-[110px]"
                              onClick={() => setContractFor(c)}
                            >
                              <FileSignature className="w-3 h-3 mr-1" /> Gerar PDF
                            </Button>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="flex-1 min-w-[110px] bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border-emerald-200"
                                  disabled={sendingPdfFor === c.id}
                                  title="Enviar por WhatsApp"
                                >
                                  {sendingPdfFor === c.id ? (
                                    <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                  ) : (
                                    <MessageCircle className="w-3 h-3 mr-1" />
                                  )}
                                  WhatsApp <ChevronDown className="w-3 h-3 ml-1" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => setPostActionStudent(c.id)}>
                                  <MessageCircle className="w-4 h-4 mr-2" />
                                  Enviar cobrança
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleSendContractPdf(c.id)}>
                                  <FileDown className="w-4 h-4 mr-2" />
                                  Enviar contrato em PDF
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openEditContract(c)}
                              title="Editar contrato"
                            >
                              <Pencil className="w-3 h-3" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-destructive hover:text-destructive"
                              onClick={() => onUnlink(c)}
                              title="Excluir plano"
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}

              {scheduled.length > 0 && (
                <div className="mt-8 space-y-3">
                  <div>
                    <h3 className="font-semibold">Contratos Agendados</h3>
                    <p className="text-xs text-muted-foreground">
                      Upgrades/renovações que passam a valer após o término do contrato vigente.
                    </p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {scheduled.map((sc: any) => {
                      const d = (v?: string | null) =>
                        v ? new Date(`${v}T00:00:00`).toLocaleDateString("pt-BR") : "—";
                      return (
                        <Card key={sc.id} className="border-amber-200 bg-amber-50/40">
                          <CardContent className="pt-5 pb-4 space-y-2">
                            <div className="font-semibold">{sc.students?.name ?? "—"}</div>
                            {sc.students?.parents?.name && (
                              <div className="text-xs text-muted-foreground">
                                Responsável: {sc.students.parents.name}
                              </div>
                            )}
                            <div className="text-sm text-muted-foreground">
                              Plano: {sc.plan_type ?? "—"} · {fmtBRL(Number(sc.amount ?? 0))}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              Vigência: <strong className="text-foreground">{d(sc.start_date)}</strong> a{" "}
                              <strong className="text-foreground">{d(sc.end_date)}</strong>
                            </div>
                            {sc.payment_method && (
                              <div className="text-xs text-muted-foreground">
                                Pagamento: {methodLabel(sc.payment_method)}
                              </div>
                            )}
                            <div className="flex gap-2 pt-1">
                              <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-300">
                                Agendado
                              </Badge>
                              <Badge
                                variant="outline"
                                className={
                                  sc.payment_status === "pago"
                                    ? "bg-blue-50 text-blue-700 border-blue-200"
                                    : "bg-amber-50 text-amber-700 border-amber-200"
                                }
                              >
                                {sc.payment_status === "pago" ? "Pago" : "Pendente"}
                              </Badge>
                            </div>
                            <div className="flex flex-wrap gap-2 mt-2">
                              <Button
                                size="sm"
                                variant="outline"
                                className="flex-1 min-w-[110px] bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border-emerald-200"
                                onClick={() => setPostActionStudent(sc.student_id)}
                              >
                                <MessageCircle className="w-3 h-3 mr-1" /> Enviar cobrança
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="planos">
          <Card>
            <CardHeader className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
              <div>
                <CardTitle>Tipos de Plano e Preços</CardTitle>
                <CardDescription>
                  Gerencie os planos oferecidos pela sua escolinha (ex: Mensal, Trimestral).
                </CardDescription>
              </div>
              <Button onClick={openNew} className="shrink-0">
                <Plus className="mr-2 h-4 w-4" /> Adicionar Plano
              </Button>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-10">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : plans.length === 0 ? (
                <div className="text-center py-12 border-2 border-dashed rounded-xl">
                  <Settings className="mx-auto h-8 w-8 text-muted-foreground/40 mb-3" />
                  <p className="text-muted-foreground">Nenhum plano configurado.</p>
                  <Button variant="link" onClick={openNew} className="mt-2">
                    Criar primeiro plano
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {plans.map((plan) => (
                    <Card key={plan.id} className="border">
                      <CardHeader className="flex flex-row items-center justify-between pb-3">
                        <CardTitle className="text-lg">{plan.plan_type}</CardTitle>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost" size="icon" className="h-8 w-8"
                            onClick={() => openEdit(plan)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost" size="icon"
                            className="h-8 w-8 text-destructive hover:text-destructive"
                            onClick={() => onDelete(plan)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </CardHeader>
                      <CardContent className="pt-0">
                        <p className="text-3xl font-bold tracking-tight mb-2">
                          {fmtBRL(Number(plan.price))}
                        </p>
                        {plan.is_social_project && (
                          <Badge className="mb-3 bg-emerald-100 text-emerald-700 border-emerald-200" variant="outline">
                            Projeto social · isento
                          </Badge>
                        )}

                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                          Métodos Aceitos:
                        </p>
                        <div className="flex flex-wrap gap-1.5">
                          {plan.accepted_payment_methods?.length ? (
                            plan.accepted_payment_methods.map((m) => (
                              <Badge key={m} variant="outline" className="font-medium">
                                {methodLabel(m)}
                              </Badge>
                            ))
                          ) : (
                            <span className="text-sm text-muted-foreground">
                              Nenhum método configurado
                            </span>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[450px] max-h-[85vh] flex flex-col p-0 gap-0">
          <DialogHeader className="px-6 pt-6 pb-2">
            <DialogTitle>{editing ? "Editar Plano" : "Novo Plano"}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-5 py-3 px-6 overflow-y-auto flex-1 min-h-0">
            <div className="grid gap-2">
              <Label htmlFor="plan_type">Nome do Plano</Label>
              {(() => {
                const presets = ["Diário", "Mensal", "Trimestral", "Semestral", "Anual"];
                const isCustom =
                  form.plan_type !== "" && !presets.includes(form.plan_type);
                const selectValue = isCustom ? "__custom__" : form.plan_type;
                return (
                  <>
                    <Select
                      value={selectValue}
                      onValueChange={(v) =>
                        setForm({ ...form, plan_type: v === "__custom__" ? " " : v })
                      }
                    >
                      <SelectTrigger id="plan_type">
                        <SelectValue placeholder="Selecione o tipo de plano" />
                      </SelectTrigger>
                      <SelectContent>
                        {presets.map((p) => (
                          <SelectItem key={p} value={p}>{p}</SelectItem>
                        ))}
                        <SelectItem value="__custom__">Outro (personalizado)…</SelectItem>
                      </SelectContent>
                    </Select>
                    {(isCustom || selectValue === "__custom__") && (
                      <Input
                        autoFocus
                        placeholder="Ex: Quinzenal, Bimestral…"
                        value={form.plan_type.trim() === "" ? "" : form.plan_type}
                        onChange={(e) => setForm({ ...form, plan_type: e.target.value })}
                      />
                    )}
                  </>
                );
              })()}
            </div>
            <div className="grid gap-2">
              <Label htmlFor="price">Preço (R$)</Label>
              <Input
                id="price" type="number" step="0.01" placeholder="0.00"
                value={form.is_social_project || form.is_trial ? "0" : form.price}
                disabled={form.is_social_project || form.is_trial}
                onChange={(e) => setForm({ ...form, price: e.target.value })}
              />
              <label className="flex items-start gap-2 text-sm cursor-pointer rounded-lg border p-3 bg-emerald-50/60 border-emerald-200">
                <Checkbox
                  checked={form.is_social_project}
                  onCheckedChange={(v) =>
                    setForm({
                      ...form,
                      is_social_project: !!v,
                      price: v ? "0" : form.price,
                      keep_active_until_paid: v ? false : form.keep_active_until_paid,
                    })
                  }
                />
                <span>
                  <strong>Projeto social</strong> — valor devido R$ 0,00. O contrato é
                  gerado já quitado, sem cobrança.
                </span>
              </label>
            </div>

            <div className="grid gap-2">
              <PaymentMethodsPicker
                value={form.accepted_payment_methods}
                onChange={(next) => setForm({ ...form, accepted_payment_methods: next })}
              />
            </div>

            <div className="grid gap-3 rounded-lg border p-3 bg-sky-50/60 border-sky-200">
              <div className="text-sm font-semibold">Disponibilidade para o responsável</div>
              <label className="flex items-start gap-2 text-sm cursor-pointer">
                <Checkbox
                  checked={form.visible_to_parents}
                  onCheckedChange={(v) =>
                    setForm({ ...form, visible_to_parents: !!v, allow_self_enroll: v ? form.allow_self_enroll : false })
                  }
                />
                <span>Plano <strong>visível</strong> no portal do responsável</span>
              </label>
              <label className="flex items-start gap-2 text-sm cursor-pointer">
                <Checkbox
                  checked={form.allow_self_enroll}
                  disabled={!form.visible_to_parents}
                  onCheckedChange={(v) => setForm({ ...form, allow_self_enroll: !!v })}
                />
                <span>
                  Permitir que o responsável <strong>escolha a turma</strong> após aderir
                  <span className="block text-xs text-muted-foreground">
                    Sem plano de pagamento o responsável nunca pode escolher turma.
                  </span>
                </span>
              </label>
            </div>

            <div className="grid gap-3 rounded-lg border p-3 bg-amber-50/60 border-amber-200">
              <label className="flex items-start gap-2 text-sm cursor-pointer">
                <Checkbox
                  checked={form.is_trial}
                  onCheckedChange={(v) =>
                    setForm({
                      ...form,
                      is_trial: !!v,
                      price: v ? "0" : form.price,
                      is_social_project: v ? false : form.is_social_project,
                      keep_active_until_paid: v ? false : form.keep_active_until_paid,
                      plan_type: v && !form.plan_type.trim() ? "Aula experimental" : form.plan_type,
                    })
                  }
                />
                <span>
                  <strong>Aula experimental</strong> — valor R$ 0,00, contrato já quitado.
                </span>
              </label>
              {form.is_trial && (
                <div className="space-y-2 pl-6">
                  <Label className="text-xs">Dias da semana disponíveis</Label>
                  <div className="flex flex-wrap gap-2">
                    {["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"].map((d, i) => {
                      const on = form.trial_days_of_week.includes(i);
                      return (
                        <button
                          key={d}
                          type="button"
                          onClick={() =>
                            setForm({
                              ...form,
                              trial_days_of_week: on
                                ? form.trial_days_of_week.filter((x) => x !== i)
                                : [...form.trial_days_of_week, i],
                            })
                          }
                          className={`text-xs px-3 py-1.5 rounded-full border transition ${on ? "bg-primary text-primary-foreground border-primary" : "bg-background hover:bg-accent"}`}
                        >
                          {d}
                        </button>
                      );
                    })}
                  </div>
                  <Label className="text-xs">Ou data específica</Label>
                  <Input
                    type="date"
                    value={form.trial_specific_date}
                    onChange={(e) => setForm({ ...form, trial_specific_date: e.target.value })}
                  />
                </div>
              )}
            </div>

            <div className="grid gap-3 rounded-lg border p-3 bg-muted/30">
              <div className="text-sm font-semibold">Regra de vencimento</div>
              <div className="grid gap-2">
                <Label className="text-xs">Tipo de vencimento</Label>
                <Select
                  value={form.due_type}
                  onValueChange={(v) => setForm({ ...form, due_type: v as "offset" | "fixed_day" })}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="offset">X dias após o fim da vigência</SelectItem>
                    <SelectItem value="fixed_day">Dia fixo do mês</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {form.due_type === "offset" ? (
                <div className="grid gap-2">
                  <Label className="text-xs">Dias após o encerramento da vigência</Label>
                  <Input
                    type="number" min="0" value={form.due_offset_days}
                    onChange={(e) => setForm({ ...form, due_offset_days: e.target.value })}
                  />
                </div>
              ) : (
                <div className="grid gap-2">
                  <Label className="text-xs">Dia do mês (1-31)</Label>
                  <Input
                    type="number" min="1" max="31" value={form.due_fixed_day}
                    onChange={(e) => setForm({ ...form, due_fixed_day: e.target.value })}
                  />
                </div>
              )}
              <label className="flex items-start gap-2 text-sm cursor-pointer">
                <Checkbox
                  checked={form.keep_active_until_paid}
                  disabled={form.is_social_project}
                  onCheckedChange={(v) => setForm({ ...form, keep_active_until_paid: !!v })}
                />
                <span>Manter contrato vigente até o pagamento ser confirmado</span>
              </label>
            </div>
          </div>
          <DialogFooter className="px-6 py-4 border-t">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={submit} disabled={saving}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={contractDialogOpen} onOpenChange={setContractDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Novo Contrato</DialogTitle>
          </DialogHeader>
          <div className="grid gap-5 py-2">
            <div className="grid gap-2">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <Label>Aluno (com responsável)</Label>
                <Button
                  type="button"
                  size="sm"
                  variant={onlyWithoutContract ? "default" : "outline"}
                  onClick={() => setOnlyWithoutContract((v) => !v)}
                >
                  {onlyWithoutContract ? "Mostrando alunos sem contrato" : "Listar alunos sem contrato"}
                </Button>
              </div>
              <Select
                value={contractForm.student_id}
                onValueChange={(v) => {
                  // Migração de plano: se o aluno já tem contrato vigente, o novo
                  // plano começa no dia seguinte ao término do atual (sem sobreposição).
                  const st: any = students.find((s: any) => s.id === v);
                  const today = brToday();
                  let start = contractForm.start_date || today;
                  if (st?.plan_end_date && st.plan_end_date >= today) {
                    const d = new Date(`${st.plan_end_date}T00:00:00`);
                    d.setDate(d.getDate() + 1);
                    start = d.toISOString().slice(0, 10);
                  }
                  const plan = plans.find((p) => p.id === contractForm.plan_price_id);
                  setContractForm({
                    ...contractForm,
                    student_id: v,
                    start_date: start,
                    end_date: plan ? computeEndDate(start, plan.validity) : contractForm.end_date,
                  });
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione um aluno" />
                </SelectTrigger>
                <SelectContent>
                  {(() => {
                    const list = onlyWithoutContract
                      ? students.filter((s: any) => !s.plan_price_id)
                      : students;
                    return list.length === 0 ? (
                    <div className="px-3 py-2 text-sm text-muted-foreground">
                      {onlyWithoutContract
                        ? "Todos os alunos já possuem contrato."
                        : "Cadastre alunos com responsável primeiro."}
                    </div>
                  ) : (
                    list.map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.name}
                        {s.parents?.name ? ` — Resp.: ${s.parents.name}` : " — (sem responsável)"}
                        {s.plan_price_id ? " · já vinculado" : ""}
                      </SelectItem>
                    ))
                  );
                  })()}
                </SelectContent>
              </Select>
            </div>

            {(() => {
              const st: any = students.find((s: any) => s.id === contractForm.student_id);
              const today = brToday();
              if (!st?.plan_end_date || st.plan_end_date < today) return null;
              return (
                <div className="rounded-md border border-amber-500 bg-amber-50 p-3 text-xs text-amber-800">
                  <b>Migração de plano.</b> Este aluno tem contrato vigente até{" "}
                  <b>{new Date(`${st.plan_end_date}T00:00:00`).toLocaleDateString("pt-BR")}</b>. Para evitar sobreposição, a vigência do novo
                  plano já foi ajustada para começar no dia seguinte. A cobrança do novo plano é
                  gerada agora.
                </div>
              );
            })()}


            <div className="grid gap-2">
              <Label>Tipo de Plano</Label>
              <Select
                value={contractForm.plan_price_id}
                onValueChange={(v) => {
                  const plan = plans.find((p) => p.id === v);
                  const end = plan
                    ? computeEndDate(contractForm.start_date, plan.validity)
                    : contractForm.end_date;
                  setContractForm({
                    ...contractForm,
                    plan_price_id: v,
                    payment_method: "",
                    end_date: end,
                  });
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione um plano" />
                </SelectTrigger>
                <SelectContent>
                  {plans.length === 0 ? (
                    <div className="px-3 py-2 text-sm text-muted-foreground">
                      Crie um plano em "Configuração de Planos".
                    </div>
                  ) : (
                    plans.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.plan_type} — {fmtBRL(Number(p.price))}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-2">
                <Label>Data Início</Label>
                <Input
                  type="date"
                  value={contractForm.start_date}
                  onChange={(e) => {
                    const start = e.target.value;
                    const plan = plans.find((p) => p.id === contractForm.plan_price_id);
                    const end = plan
                      ? computeEndDate(start, plan.validity)
                      : contractForm.end_date;
                    setContractForm({ ...contractForm, start_date: start, end_date: end });
                  }}
                />
              </div>
              <div className="grid gap-2">
                <Label>Data Fim</Label>
                <Input
                  type="date"
                  value={contractForm.end_date}
                  onChange={(e) => setContractForm({ ...contractForm, end_date: e.target.value })}
                />
              </div>
            </div>


            {(() => {
              const plan = plans.find((p) => p.id === contractForm.plan_price_id);
              const period = (plan?.validity || plan?.plan_type || "").toLowerCase();
              const renewable = ["mensal", "trimestral", "semestral", "anual"].some((v) =>
                period.includes(v),
              );
              if (!renewable) return null;
              return (
                <label className="flex items-start gap-2 text-sm cursor-pointer rounded-lg border p-3 bg-muted/30">
                  <Checkbox
                    checked={contractForm.auto_renew}
                    onCheckedChange={(v) =>
                      setContractForm({ ...contractForm, auto_renew: !!v })
                    }
                  />
                  <span>Renovar automaticamente pelo mesmo período ao expirar</span>
                </label>
              );
            })()}



            <div className="grid gap-2">
              <Label>Forma de Pagamento</Label>
              <Select
                value={contractForm.payment_method}
                onValueChange={(v) => setContractForm({ ...contractForm, payment_method: v })}
                disabled={!contractForm.plan_price_id}
              >
                <SelectTrigger>
                  <SelectValue
                    placeholder={
                      contractForm.plan_price_id
                        ? "Selecione a forma de pagamento"
                        : "Escolha um plano primeiro"
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {(() => {
                    const plan = plans.find((p) => p.id === contractForm.plan_price_id);
                    const allowed = plan?.accepted_payment_methods?.length
                      ? PAYMENT_METHODS.filter((m) =>
                          plan!.accepted_payment_methods.includes(m.value),
                        )
                      : PAYMENT_METHODS;
                    return allowed.map((m) => (
                      <SelectItem key={m.value} value={m.value}>
                        {m.label}
                      </SelectItem>
                    ));
                  })()}
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="discount" className="flex items-center gap-2">
                <Percent className="h-4 w-4 text-emerald-600" />
                Desconto no Plano (R$)
              </Label>
              <Input
                id="discount"
                type="number"
                step="0.01"
                min="0"
                value={contractForm.discount}
                onChange={(e) => setContractForm({ ...contractForm, discount: e.target.value })}
                placeholder="0.00"
              />
              {(() => {
                const plan = plans.find((p) => p.id === contractForm.plan_price_id);
                if (!plan) return null;
                const base = Number(plan.price);
                const disc = Number(contractForm.discount || 0);
                const fin = Math.max(0, base - disc);
                return (
                  <p className="text-xs text-muted-foreground">
                    Valor final: <span className="font-semibold">{fmtBRL(fin)}</span>
                    {disc > 0 && (
                      <span className="ml-1 text-emerald-700">(- {fmtBRL(disc)})</span>
                    )}
                  </p>
                );
              })()}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setContractDialogOpen(false)}>Cancelar</Button>
            <Button onClick={submitContract} disabled={savingContract}>
              {savingContract && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Salvar Contrato
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ContractTemplateConfigDialog open={tplConfigOpen} onClose={() => setTplConfigOpen(false)} />

      <BulkContractDialog
        open={bulkOpen}
        onOpenChange={setBulkOpen}
        plans={plans}
        students={students}
        onDone={() => reload()}
      />

      <ContractDialog
        open={!!contractFor}
        onClose={() => setContractFor(null)}
        student={
          contractFor
            ? {
                id: contractFor.id,
                name: contractFor.name,
                cpf: contractFor.cpf,
                birth_date: contractFor.birth_date,
                parent: contractFor.parents
                  ? {
                      id: contractFor.parents.id,
                      name: contractFor.parents.name,
                      cpf: contractFor.parents.cpf,
                      phone: contractFor.parents.phone,
                      email: contractFor.parents.email,
                      address: [
                        contractFor.parents.street,
                        contractFor.parents.number,
                        contractFor.parents.neighborhood,
                        contractFor.parents.city,
                        contractFor.parents.state,
                        contractFor.parents.cep,
                      ].filter(Boolean).join(", "),
                    }
                  : null,
              }
            : null
        }
        plan={contractFor?.plan_prices ?? null}
        onSaved={reload}
      />

      <PostContractActionModal
        open={!!postActionStudent}
        studentId={postActionStudent}
        onClose={() => setPostActionStudent(null)}
      />
    </div>
  );
}
